import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import type { PostStatus, PublicPostDetail } from '@news-cms/shared';
import { absolutizeMediaUrls } from '@/lib/media';

const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

interface ApiResponse {
  data?: { post: PublicPostDetail; status: PostStatus };
  error?: { code: string; message: string };
}

interface PreviewState {
  status: 'loading' | 'ok' | 'expired' | 'not-found' | 'error';
  post?: PublicPostDetail;
  postStatus?: PostStatus;
  errorMessage?: string;
}

export const Route = createFileRoute('/preview/$token')({
  component: PreviewPage,
});

const STATUS_LABEL: Record<PostStatus, string> = {
  draft: 'ฉบับร่าง',
  pending_review: 'รออนุมัติ',
  scheduled: 'ตั้งเวลาเผยแพร่',
  published: 'เผยแพร่แล้ว',
  archived: 'เก็บถาวร',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Public preview page — viewable without login via a signed token URL.
 * Fetches via the public preview endpoint (which returns draft posts
 * for valid tokens) and renders with the same tiptap-content layout
 * as the in-editor preview dialog.
 *
 * Not under _authenticated on purpose — the whole point is sharing
 * with people who don't have an admin account. Token grant + expiry
 * enforced server-side via JWT verification.
 */
function PreviewPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<PreviewState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/public/preview/${token}`);
        const json = (await res.json().catch(() => null)) as ApiResponse | null;
        if (cancelled) return;

        if (res.status === 401) {
          setState({ status: 'expired' });
          return;
        }
        if (res.status === 404) {
          setState({ status: 'not-found' });
          return;
        }
        if (!res.ok || !json?.data) {
          setState({
            status: 'error',
            errorMessage: json?.error?.message ?? `HTTP ${res.status}`,
          });
          return;
        }
        setState({ status: 'ok', post: json.data.post, postStatus: json.data.status });
      } catch (e) {
        if (!cancelled) {
          setState({ status: 'error', errorMessage: String(e) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === 'loading') {
    return <CenterMessage>กำลังโหลดพรีวิว...</CenterMessage>;
  }
  if (state.status === 'expired') {
    return (
      <CenterMessage>
        ลิงก์พรีวิวหมดอายุหรือไม่ถูกต้อง — ติดต่อผู้ส่งลิงก์เพื่อสร้างลิงก์ใหม่
      </CenterMessage>
    );
  }
  if (state.status === 'not-found') {
    return <CenterMessage>ไม่พบบทความ — อาจถูกย้ายไปถังขยะแล้ว</CenterMessage>;
  }
  if (state.status === 'error' || !state.post) {
    return <CenterMessage>เกิดข้อผิดพลาด: {state.errorMessage ?? 'ไม่ทราบสาเหตุ'}</CenterMessage>;
  }

  const post = state.post;
  const bodyHtml = absolutizeMediaUrls(post.bodyHtml ?? '');
  const displayDate = post.publishedAt;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
        <Eye className="mr-1 inline h-3.5 w-3.5" />
        พรีวิวบทความ — สถานะ:{' '}
        <strong>{state.postStatus ? STATUS_LABEL[state.postStatus] : '—'}</strong> ·
        ลิงก์นี้ใช้ได้ชั่วคราว
      </div>
      <article className="mx-auto max-w-3xl space-y-4 px-4 py-8">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold leading-tight text-slate-900 dark:text-slate-50">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            <span>{post.author.name}</span>
            <span>·</span>
            <span>{formatDate(displayDate)}</span>
            {post.readingTimeMinutes > 0 && (
              <>
                <span>·</span>
                <span>อ่าน ~{post.readingTimeMinutes} นาที</span>
              </>
            )}
          </div>
        </header>
        {post.summary && (
          <p className="border-l-2 border-slate-300 pl-3 text-base italic text-slate-600 dark:border-slate-700 dark:text-slate-400">
            {post.summary}
          </p>
        )}
        <div
          className="tiptap-content text-base leading-relaxed text-slate-800 dark:text-slate-200"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      </article>
    </div>
  );
}

function CenterMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4 text-center text-sm text-slate-600 dark:bg-slate-950 dark:text-slate-300">
      {children}
    </div>
  );
}
